import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  BookUser,
  Download,
  Edit,
  Maximize2,
  MoreVertical,
  Pin,
  Plus,
  RotateCcw,
  Search,
  Settings,
  Star,
  Trash2,
} from 'lucide-react';
import {
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';
import { DataModeBadge } from '@/components/DataModeBadge';
import { AddContactDialog } from '@/components/shared/add-contact-dialog';
import { ColumnConfigDialog, type ColumnDefinition } from '@/components/shared/column-config-dialog';
import { CustomerLink } from '@/components/shared/customer-link';
import { CustomerSearchMultiSelect } from '@/components/shared/customer-search-multi-select';
import { useCanWrite } from '@/components/auth/useCanWrite';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MultiSelect } from '@/components/ui/multiselect';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { UniqueTableNamesForColumnConfiguration } from '@/config/unique-table-names-for-column-configuration';
import { useContactTitles } from '@/hooks/useContactTitles';
import type { CreatePromiseFormCustomer } from '@/hooks/useCreatePromiseData';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import {
  useAllContactsGlobal,
  buildGlobalContactsFilterVariable,
  useGlobalContactMutations,
} from '@/hooks/useAllContactsGlobal';
import { useExportAll } from '@/hooks/useExportAll';
import { useModeAwareExport } from '@/hooks/useModeAwareExport';
import { useRiskLevels } from '@/hooks/useRiskLevelsData';
import { useScreenColumnPreferences } from '@/hooks/useScreenColumnPreferences';
import { contactDisplayName } from '@/lib/contact-display-name';
import { validateContactForm } from '@/lib/contact-form-validation';
import { getFilteredGlobalContactsUI } from '@/lib/demo-data/global-contacts';
import { getPinnedColumnStyle, useColumnWidthObserver } from '@/lib/table-pinning';
import { formatDateTime } from '@/lib/utils';
import { ExportProvider } from '@/providers/ExportProvider';
import type { ColumnInput } from '@/types/export';
import type { GlobalContactsQueryParams, GlobalContactUI } from '@/types/ui/customer-detail';

const DEFAULT_GLOBAL_CONTACT_COLUMNS: ColumnDefinition[] = [
  { id: 'isPrimary', label: 'Primary', visible: true, order: 0 },
  { id: 'accountNumber', label: 'Account No', visible: true, order: 1 },
  { id: 'customerName', label: 'Customer', visible: true, order: 2 },
  { id: 'firstName', label: 'First Name', visible: true, order: 3 },
  { id: 'surname', label: 'Surname', visible: true, order: 4 },
  { id: 'email', label: 'Email', visible: true, order: 5 },
  { id: 'phone', label: 'Phone', visible: true, order: 6 },
  { id: 'jobTitle', label: 'Job Title', visible: true, order: 7 },
  { id: 'lastContact', label: 'Last Contact', visible: true, order: 8 },
  { id: 'title', label: 'Title', visible: false, order: 9 },
  { id: 'mobile', label: 'Mobile', visible: false, order: 10 },
  { id: 'preferredContactMethod', label: 'Preferred Contact Method', visible: false, order: 11 },
  { id: 'company', label: 'Company', visible: false, order: 12 },
  { id: 'notes', label: 'Notes', visible: false, order: 13 },
  { id: 'createdAt', label: 'Created At', visible: false, order: 14 },
];

const SORT_FIELD_BY_COLUMN: Record<string, string> = {
  firstName: 'firstName',
  surname: 'surname',
  email: 'email',
  phone: 'phone',
  mobile: 'mobile',
  jobTitle: 'jobTitle',
  company: 'company',
  customerName: 'customerName',
  accountNumber: 'customerAccountNumber',
  lastContact: 'lastContact',
  createdAt: 'createdAt',
};

// Maps the table's column ids to the export model's property names so the
// server-side export job (v_GlobalContact_List) resolves each visible column.
const EXPORT_COLUMN_NAME_MAP: Record<string, string> = {
  isPrimary: 'IsPrimary',
  accountNumber: 'CustomerAccountNumber',
  customerName: 'CustomerName',
  firstName: 'FirstName',
  surname: 'Surname',
  email: 'Email',
  phone: 'Phone',
  jobTitle: 'JobTitle',
  lastContact: 'LastContactDate',
  title: 'Title',
  mobile: 'Mobile',
  preferredContactMethod: 'PreferredContactMethod',
  company: 'Company',
  notes: 'Notes',
  createdAt: 'CreatedAt',
};

// When a column is pinned right, the trailing Actions column is treated as the
// rightmost pinned column so pinned data columns are offset by its width
// instead of sliding underneath it.
const ACTIONS_COLUMN_ID = '__actions';

const PREFERRED_CONTACT_METHOD_OPTIONS = ['Email', 'Phone', 'Customer Portal'];
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;

type SortDirection = 'asc' | 'desc';

interface EditFormData {
  title: string;
  firstName: string;
  surname: string;
  email: string;
  phone: string;
  mobile: string;
  preferredContactMethod: string;
  jobTitle: string;
  company: string;
  notes: string;
  isPrimary: boolean;
}

const EMPTY_EDIT_FORM: EditFormData = {
  title: '',
  firstName: '',
  surname: '',
  email: '',
  phone: '',
  mobile: '',
  preferredContactMethod: '',
  jobTitle: '',
  company: '',
  notes: '',
  isPrimary: false,
};

function getCellText(contact: GlobalContactUI, columnId: string): string {
  switch (columnId) {
    case 'isPrimary':
      return contact.isPrimary ? 'Yes' : 'No';
    case 'accountNumber':
      return contact.customerAccountNumber;
    case 'customerName':
      return contact.customerName;
    case 'title':
      return contact.title ?? '';
    case 'firstName':
      return contact.firstName;
    case 'surname':
      return contact.surname;
    case 'email':
      return contact.email;
    case 'phone':
      return contact.phone;
    case 'mobile':
      return contact.mobile ?? '';
    case 'preferredContactMethod':
      return contact.preferredContactMethod ?? '';
    case 'jobTitle':
      return contact.jobTitle;
    case 'company':
      return contact.company;
    case 'notes':
      return contact.notes;
    case 'lastContact':
      return contact.lastContact ? formatDateTime(contact.lastContact) : '';
    case 'createdAt':
      return contact.createdAt ? formatDateTime(contact.createdAt) : '';
    default:
      return '';
  }
}

function escapeCsv(value: string): string {
  if (value === '') return '';
  const needsQuoting = /[",\n\r]/.test(value);
  const escaped = value.replace(/"/g, '""');
  return needsQuoting ? `"${escaped}"` : escaped;
}

function downloadCsv(fileName: string, content: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

export function ContactsPage() {
  const { updateContact, deleteContact, setContactAsPrimary, refreshGlobalContacts } = useGlobalContactMutations();
  const { data: riskLevels = [] } = useRiskLevels();
  const { data: titleOptions = [] } = useContactTitles();

  // Filters
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebouncedValue(searchInput, 300);
  const [customerFilter, setCustomerFilter] = useState<CreatePromiseFormCustomer[]>([]);
  const [riskLevelFilter, setRiskLevelFilter] = useState<string[]>([]);
  const [primaryOnly, setPrimaryOnly] = useState(false);
  const [hasEmail, setHasEmail] = useState(false);

  // Sorting & pagination
  const [sortColumn, setSortColumn] = useState('surname');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Reset to first page whenever the result set changes shape
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, customerFilter, riskLevelFilter, primaryOnly, hasEmail, sortColumn, sortDirection, pageSize]);

  const queryParams = useMemo<GlobalContactsQueryParams>(
    () => ({
      search: debouncedSearch,
      customerIds: customerFilter.map((c) => c.id),
      riskLevelIds: riskLevelFilter.map((id) => Number(id)),
      primaryOnly,
      hasEmail,
      sortField: SORT_FIELD_BY_COLUMN[sortColumn] ?? 'surname',
      sortDirection,
      page,
      pageSize,
    }),
    [
      debouncedSearch,
      customerFilter,
      riskLevelFilter,
      primaryOnly,
      hasEmail,
      sortColumn,
      sortDirection,
      page,
      pageSize,
    ],
  );

  const { data, isLoading } = useAllContactsGlobal(queryParams);
  const contacts = data?.contacts ?? [];
  const totalCount = data?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // Column preferences
  const [isColumnConfigOpen, setIsColumnConfigOpen] = useState(false);
  const {
    columns,
    visibleColumns,
    savePreferences,
    resetToDefaults,
    columnOrder,
    columnWidths,
    pinnedLeft,
    pinnedRight,
    setColumnOrder,
    setColumnWidths,
    setPinned,
    resetColumnOrder,
    resetColumnWidths,
  } = useScreenColumnPreferences(UniqueTableNamesForColumnConfiguration.GlobalContacts, DEFAULT_GLOBAL_CONTACT_COLUMNS);
  const baseVisibleColumnIds = visibleColumns.map((col) => col.id);
  const visibleColumnIds =
    columnOrder.length > 0
      ? columnOrder
          .filter((id) => baseVisibleColumnIds.includes(id))
          .concat(baseVisibleColumnIds.filter((id) => !columnOrder.includes(id)))
      : baseVisibleColumnIds;

  // Column resize / reorder / pinning
  const { widths: measuredColumnWidths, measureRef } = useColumnWidthObserver();
  const isActionsPinned = pinnedRight.some((id) => visibleColumnIds.includes(id));
  const pinnedRightWithActions = isActionsPinned ? [...pinnedRight, ACTIONS_COLUMN_ID] : pinnedRight;
  const pinLayoutColumnOrder = [...visibleColumnIds, ACTIONS_COLUMN_ID];
  const resizingRef = useRef<{ columnId: string; startX: number; startWidth: number } | null>(null);
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);
  const [localColumnWidths, setLocalColumnWidths] = useState<Record<string, number>>(columnWidths);
  const localColumnWidthsRef = useRef(localColumnWidths);
  localColumnWidthsRef.current = localColumnWidths;

  useEffect(() => {
    setLocalColumnWidths(columnWidths);
  }, [columnWidths]);

  const handleResizeMouseDown = (e: ReactMouseEvent, columnId: string, currentWidth: number) => {
    e.preventDefault();
    e.stopPropagation();
    resizingRef.current = { columnId, startX: e.clientX, startWidth: currentWidth };
    const handleMouseMove = (ev: MouseEvent) => {
      const current = resizingRef.current;
      if (!current) return;
      const newWidth = Math.max(60, current.startWidth + (ev.clientX - current.startX));
      setLocalColumnWidths((prev) => ({ ...prev, [current.columnId]: newWidth }));
    };
    const handleMouseUp = () => {
      if (resizingRef.current) {
        setColumnWidths(localColumnWidthsRef.current);
      }
      resizingRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handlePinLeft = (columnId: string) => {
    const newPinnedLeft = pinnedLeft.includes(columnId)
      ? pinnedLeft.filter((id) => id !== columnId)
      : [...pinnedLeft, columnId];
    setPinned(
      newPinnedLeft,
      pinnedRight.filter((id) => id !== columnId),
    );
  };

  const handlePinRight = (columnId: string) => {
    const newPinnedRight = pinnedRight.includes(columnId)
      ? pinnedRight.filter((id) => id !== columnId)
      : [...pinnedRight, columnId];
    setPinned(
      pinnedLeft.filter((id) => id !== columnId),
      newPinnedRight,
    );
  };

  const handleColumnDrop = (columnId: string) => {
    if (!draggedColumn || draggedColumn === columnId) return;
    const newCols = [...visibleColumnIds];
    const fromIdx = newCols.indexOf(draggedColumn);
    const toIdx = newCols.indexOf(columnId);
    newCols.splice(fromIdx, 1);
    newCols.splice(toIdx, 0, draggedColumn);
    setColumnOrder(newCols);
    setDraggedColumn(null);
    setDragOverColumn(null);
  };

  // Side panel + dialogs
  const [selectedContact, setSelectedContact] = useState<GlobalContactUI | null>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editForm, setEditForm] = useState<EditFormData>(EMPTY_EDIT_FORM);

  // Add contact flow
  const [isAddContactOpen, setIsAddContactOpen] = useState(false);
  const { canWrite } = useCanWrite();

  const getColumnLabel = (columnId: string) =>
    DEFAULT_GLOBAL_CONTACT_COLUMNS.find((col) => col.id === columnId)?.label ?? columnId;

  const isSortable = (columnId: string) => columnId in SORT_FIELD_BY_COLUMN;

  const handleSort = (columnId: string) => {
    if (!isSortable(columnId)) return;
    if (sortColumn === columnId) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(columnId);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (columnId: string) => {
    if (sortColumn !== columnId) return <ArrowUpDown className="ml-2 h-4 w-4" />;
    return sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4" /> : <ArrowDown className="ml-2 h-4 w-4" />;
  };

  const openPanel = (contact: GlobalContactUI) => {
    setSelectedContact(contact);
    setIsPanelOpen(true);
  };

  const openEditDialog = (contact: GlobalContactUI) => {
    setSelectedContact(contact);
    setEditForm({
      title: contact.title ?? '',
      firstName: contact.firstName,
      surname: contact.surname,
      email: contact.email || '',
      phone: contact.phone || '',
      mobile: contact.mobile ?? '',
      preferredContactMethod: contact.preferredContactMethod ?? '',
      jobTitle: contact.jobTitle,
      company: contact.company,
      notes: contact.notes,
      isPrimary: contact.isPrimary,
    });
    setIsEditOpen(true);
  };

  const handleEditSubmit = async () => {
    if (!selectedContact || isSaving) return;
    const validationError = validateContactForm(editForm);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setIsSaving(true);
    try {
      await updateContact(selectedContact.customerId, selectedContact.id, {
        title: editForm.title,
        firstName: editForm.firstName,
        surname: editForm.surname,
        email: editForm.email,
        phone: editForm.phone,
        mobile: editForm.mobile,
        preferredContactMethod: editForm.preferredContactMethod,
        jobTitle: editForm.jobTitle,
        company: editForm.company,
        notes: editForm.notes,
        isPrimary: editForm.isPrimary,
      });
      setIsEditOpen(false);
      toast.success('Contact updated successfully');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update contact');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedContact || isDeleting) return;
    setIsDeleting(true);
    try {
      await deleteContact(selectedContact.customerId, selectedContact.id);
      setIsDeleteOpen(false);
      setIsPanelOpen(false);
      toast.success('Contact deleted successfully');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to delete contact');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSetPrimary = async (contact: GlobalContactUI) => {
    try {
      await setContactAsPrimary(contact.customerId, contact.id);
      toast.success(`${contactDisplayName(contact)} set as primary contact`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to set primary contact');
    }
  };

  // Live export follows the app's server-side export-job pattern: a background
  // job renders every filtered row (visible columns) via createExportJob using
  // the same GraphQL filter as the list. Demo mode builds the CSV client-side so
  // it stays timezone-correct and works without a backend.
  const getExportColumns = useCallback(
    (): ColumnInput[] =>
      visibleColumns
        .filter((col) => col.id in EXPORT_COLUMN_NAME_MAP)
        .map((col) => ({ name: EXPORT_COLUMN_NAME_MAP[col.id], displayName: col.label })),
    [visibleColumns],
  );

  const getExportFilter = useCallback(
    (): Record<string, unknown> | undefined => buildGlobalContactsFilterVariable(queryParams),
    [queryParams],
  );

  const { triggerExportAll, isExporting } = useExportAll({
    form: 'Contacts',
    getColumns: getExportColumns,
    getFilter: getExportFilter,
    fileName: `contacts-export-${new Date().toISOString().split('T')[0]}`,
    onFinished: () => toast.success('Export completed successfully'),
  });

  const exportDemoCsv = useCallback(() => {
    const rows = getFilteredGlobalContactsUI({ ...queryParams, page: 1, pageSize: totalCount || undefined });
    const header = visibleColumns.map((col) => escapeCsv(col.label)).join(',');
    const lines = rows.map((row) => visibleColumns.map((col) => escapeCsv(getCellText(row, col.id))).join(','));
    downloadCsv('contacts-export.csv', [header, ...lines].join('\n'));
    toast.success('Export downloaded');
  }, [queryParams, totalCount, visibleColumns]);

  const handleExport = useModeAwareExport(exportDemoCsv, triggerExportAll);

  const renderCell = (contact: GlobalContactUI, columnId: string) => {
    switch (columnId) {
      case 'isPrimary':
        return contact.isPrimary ? (
          <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" aria-label="Primary contact" />
        ) : null;
      case 'accountNumber':
        return contact.customerAccountNumber ? (
          <CustomerLink customerId={contact.customerId} label={contact.customerAccountNumber} />
        ) : (
          '-'
        );
      case 'customerName':
        return contact.customerName ? (
          <CustomerLink customerId={contact.customerId} label={contact.customerName} />
        ) : (
          '-'
        );
      case 'notes':
        return (
          <div className="max-w-xs truncate" title={contact.notes}>
            {contact.notes || '-'}
          </div>
        );
      default: {
        const text = getCellText(contact, columnId);
        return text || '-';
      }
    }
  };

  const riskLevelOptions = riskLevels.map((level) => ({ value: String(level.id), label: level.name }));

  return (
    <div className="flex flex-col gap-4 p-4 h-full overflow-hidden">
      <div className="flex items-center gap-3">
        <BookUser className="h-7 w-7 text-teal-600" />
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold">Contacts</h1>
            <DataModeBadge />
          </div>
          <p className="text-muted-foreground">Search, view, and manage contacts across all customers</p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative w-72 max-w-full">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search name, account, email, phone, job title..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-9"
              aria-label="Search contacts"
            />
          </div>
          <div className="w-64 max-w-full">
            <CustomerSearchMultiSelect
              selectedCustomers={customerFilter}
              onSelectionChange={setCustomerFilter}
              placeholder="Filter by customer..."
            />
          </div>
          <div className="w-52 max-w-full">
            <MultiSelect
              options={riskLevelOptions}
              value={riskLevelFilter}
              onValueChange={setRiskLevelFilter}
              placeholder="Filter by risk level..."
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={primaryOnly}
              onCheckedChange={(checked) => setPrimaryOnly(checked === true)}
              aria-label="Primary contacts only"
            />
            Primary only
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={hasEmail}
              onCheckedChange={(checked) => setHasEmail(checked === true)}
              aria-label="Has email"
            />
            Has email
          </label>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => setIsColumnConfigOpen(true)}
            title="Configure columns"
            aria-label="Configure columns"
          >
            <Settings className="h-4 w-4" />
          </Button>
          <Button variant="outline" onClick={handleExport} disabled={isExporting || totalCount === 0}>
            <Download className="mr-2 h-4 w-4" />
            {isExporting ? 'Exporting...' : 'Export'}
          </Button>
          {canWrite && (
            <Button onClick={() => setIsAddContactOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Contact
            </Button>
          )}
        </div>
      </div>

      {/* Table. The scroll container is the div, not the Table component: its own
          overflow-x-auto wrapper would put the horizontal scrollbar below the last
          row, out of view whenever the rows overflow vertically. */}
      <div className="rounded-md border flex-1 min-h-0 overflow-auto">
        <table className="w-full caption-bottom text-sm">
          <TableHeader>
            <TableRow>
              {visibleColumnIds.map((columnId) => {
                const isPinLeft = pinnedLeft.includes(columnId);
                const isPinRight = pinnedRight.includes(columnId);
                const isDragOver = dragOverColumn === columnId && draggedColumn !== columnId;
                const colWidth = localColumnWidths[columnId];
                return (
                  <th
                    key={columnId}
                    ref={measureRef(columnId)}
                    draggable
                    onDragStart={() => setDraggedColumn(columnId)}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragOverColumn(columnId);
                    }}
                    onDragEnd={() => {
                      setDraggedColumn(null);
                      setDragOverColumn(null);
                    }}
                    onDrop={() => handleColumnDrop(columnId)}
                    style={getPinnedColumnStyle(columnId, pinnedLeft, pinnedRightWithActions, localColumnWidths, {
                      zIndex: 20,
                      unpinnedPosition: 'relative',
                      measuredWidths: measuredColumnWidths,
                      columnOrder: pinLayoutColumnOrder,
                    })}
                    className={`h-10 px-4 text-left font-bold text-gray-700 border-r border-gray-200 last:border-r-0 select-none whitespace-nowrap bg-white
                      ${columnId === 'isPrimary' && !colWidth ? 'w-[60px]' : ''}
                      ${isDragOver ? 'bg-blue-50 border-l-2 border-l-blue-400' : ''}
                      ${draggedColumn === columnId ? 'opacity-50' : ''}
                      ${isPinLeft || isPinRight ? 'shadow-md' : ''}`}
                  >
                    <div className="flex items-center justify-between gap-1 w-full">
                      {isSortable(columnId) ? (
                        <button
                          type="button"
                          onClick={() => handleSort(columnId)}
                          className="flex items-center hover:text-gray-900 transition-colors font-bold"
                        >
                          <span>{getColumnLabel(columnId)}</span>
                          <span className="text-gray-500">{getSortIcon(columnId)}</span>
                        </button>
                      ) : (
                        <span className="font-bold">{getColumnLabel(columnId)}</span>
                      )}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            aria-label={`${getColumnLabel(columnId)} column options`}
                            className="p-0.5 rounded hover:bg-gray-100 transition-colors cursor-pointer flex-shrink-0"
                          >
                            <MoreVertical className="h-4 w-4 text-gray-300 hover:text-gray-500" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="z-[9999] w-48">
                          <DropdownMenuItem onClick={() => handlePinLeft(columnId)}>
                            <Pin className="h-4 w-4 mr-2" />
                            {isPinLeft ? 'Unpin from Left' : 'Pin to Left'}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handlePinRight(columnId)}>
                            <Pin className="h-4 w-4 mr-2 rotate-180" />
                            {isPinRight ? 'Unpin from Right' : 'Pin to Right'}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => resetColumnWidths()}>
                            <Maximize2 className="h-4 w-4 mr-2" />
                            Reset Column Widths
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => resetColumnOrder()}>
                            <RotateCcw className="h-4 w-4 mr-2" />
                            Reset Column Order
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    {/* Resize handle */}
                    <button
                      type="button"
                      aria-label="Resize column"
                      className="absolute right-0 top-0 h-full w-2 cursor-col-resize hover:bg-blue-300 opacity-0 hover:opacity-60 transition-opacity z-10 p-0 border-0 bg-transparent"
                      onMouseDown={(e) =>
                        handleResizeMouseDown(
                          e,
                          columnId,
                          colWidth || (e.currentTarget.closest('th') as HTMLElement)?.offsetWidth || 150,
                        )
                      }
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    />
                  </th>
                );
              })}
              <TableHead
                ref={measureRef(ACTIONS_COLUMN_ID)}
                style={getPinnedColumnStyle(ACTIONS_COLUMN_ID, pinnedLeft, pinnedRightWithActions, localColumnWidths, {
                  zIndex: 20,
                  measuredWidths: measuredColumnWidths,
                  columnOrder: pinLayoutColumnOrder,
                })}
                className={`w-[80px] ${isActionsPinned ? 'bg-white shadow-md' : ''}`}
              >
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={visibleColumnIds.length + 1} className="text-center text-muted-foreground">
                  Loading contacts...
                </TableCell>
              </TableRow>
            ) : contacts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={visibleColumnIds.length + 1} className="text-center text-muted-foreground">
                  No contacts found
                </TableCell>
              </TableRow>
            ) : (
              contacts.map((contact) => (
                <TableRow
                  key={contact.id}
                  className="cursor-pointer"
                  role="button"
                  tabIndex={0}
                  aria-label={`View details for ${contactDisplayName(contact)}`}
                  onClick={() => openPanel(contact)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openPanel(contact);
                    }
                  }}
                >
                  {visibleColumnIds.map((columnId) => {
                    const isPinned = pinnedLeft.includes(columnId) || pinnedRight.includes(columnId);
                    return (
                      <TableCell
                        key={columnId}
                        style={getPinnedColumnStyle(columnId, pinnedLeft, pinnedRightWithActions, localColumnWidths, {
                          measuredWidths: measuredColumnWidths,
                          columnOrder: pinLayoutColumnOrder,
                        })}
                        className={`${['firstName', 'surname'].includes(columnId) ? 'font-medium' : ''} ${
                          isPinned ? 'shadow-md bg-white' : ''
                        }`}
                      >
                        {renderCell(contact, columnId)}
                      </TableCell>
                    );
                  })}
                  <TableCell
                    style={getPinnedColumnStyle(
                      ACTIONS_COLUMN_ID,
                      pinnedLeft,
                      pinnedRightWithActions,
                      localColumnWidths,
                      { measuredWidths: measuredColumnWidths, columnOrder: pinLayoutColumnOrder },
                    )}
                    className={isActionsPinned ? 'bg-white shadow-md' : ''}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" aria-label="Contact actions">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {!contact.isPrimary && (
                          <DropdownMenuItem onClick={() => handleSetPrimary(contact)}>
                            <Star className="mr-2 h-4 w-4" />
                            Set as Primary
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => openEditDialog(contact)}>
                          <Edit className="mr-2 h-4 w-4" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => {
                            setSelectedContact(contact);
                            setIsDeleteOpen(true);
                          }}
                          className="text-destructive"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">
          {totalCount} contact{totalCount === 1 ? '' : 's'} — Page {page} of {totalPages}
        </span>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">Rows per page:</span>
          <Select value={String(pageSize)} onValueChange={(value) => setPageSize(Number(value))}>
            <SelectTrigger className="h-8 w-[80px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
          >
            Next
          </Button>
        </div>
      </div>

      {/* Contact detail side panel */}
      <Sheet open={isPanelOpen} onOpenChange={setIsPanelOpen}>
        <SheetContent className="overflow-y-auto sm:max-w-md">
          {selectedContact && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  {selectedContact.isPrimary && <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />}
                  {contactDisplayName(selectedContact)}
                </SheetTitle>
                <SheetDescription>{selectedContact.jobTitle || 'Contact details'}</SheetDescription>
              </SheetHeader>
              <div className="space-y-4 px-4 py-4 text-sm">
                <DetailRow label="Customer">
                  <CustomerLink customerId={selectedContact.customerId} label={selectedContact.customerName || '-'} />
                </DetailRow>
                <DetailRow label="Account No">
                  {selectedContact.customerAccountNumber ? (
                    <CustomerLink
                      customerId={selectedContact.customerId}
                      label={selectedContact.customerAccountNumber}
                    />
                  ) : (
                    '-'
                  )}
                </DetailRow>
                <DetailRow label="Email">{selectedContact.email || '-'}</DetailRow>
                <DetailRow label="Phone">{selectedContact.phone || '-'}</DetailRow>
                <DetailRow label="Mobile">{selectedContact.mobile || '-'}</DetailRow>
                <DetailRow label="Preferred Contact Method">{selectedContact.preferredContactMethod || '-'}</DetailRow>
                <DetailRow label="Company">{selectedContact.company || '-'}</DetailRow>
                <DetailRow label="Last Contact">
                  {selectedContact.lastContact ? formatDateTime(selectedContact.lastContact) : '-'}
                </DetailRow>
                <DetailRow label="Notes">{selectedContact.notes || '-'}</DetailRow>
              </div>
              <SheetFooter className="flex-row flex-wrap gap-2">
                {!selectedContact.isPrimary && (
                  <Button variant="outline" onClick={() => handleSetPrimary(selectedContact)}>
                    <Star className="mr-2 h-4 w-4" />
                    Set as Primary
                  </Button>
                )}
                <Button variant="outline" onClick={() => openEditDialog(selectedContact)}>
                  <Edit className="mr-2 h-4 w-4" />
                  Edit
                </Button>
                <Button variant="destructive" onClick={() => setIsDeleteOpen(true)}>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </Button>
              </SheetFooter>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Add contact dialog (global — includes customer picker) */}
      <AddContactDialog
        open={isAddContactOpen}
        onOpenChange={setIsAddContactOpen}
        onContactCreated={() => {
          refreshGlobalContacts();
          setIsAddContactOpen(false);
        }}
      />

      {/* Edit dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Contact</DialogTitle>
            <DialogDescription>Update contact information. Complete at least one field.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-title">Title</Label>
                <Select value={editForm.title} onValueChange={(value) => setEditForm({ ...editForm, title: value })}>
                  <SelectTrigger id="edit-title">
                    <SelectValue placeholder="Select title" />
                  </SelectTrigger>
                  <SelectContent>
                    {titleOptions.map((title) => (
                      <SelectItem key={title} value={title}>
                        {title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-firstName">First Name</Label>
                <Input
                  id="edit-firstName"
                  value={editForm.firstName}
                  onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-surname">Surname</Label>
                <Input
                  id="edit-surname"
                  value={editForm.surname}
                  onChange={(e) => setEditForm({ ...editForm, surname: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-phone">Phone</Label>
                <Input
                  id="edit-phone"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-mobile">Mobile</Label>
                <Input
                  id="edit-mobile"
                  value={editForm.mobile}
                  onChange={(e) => setEditForm({ ...editForm, mobile: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-preferredContactMethod">Preferred Contact Method</Label>
                <Select
                  value={editForm.preferredContactMethod}
                  onValueChange={(value) => setEditForm({ ...editForm, preferredContactMethod: value })}
                >
                  <SelectTrigger id="edit-preferredContactMethod">
                    <SelectValue placeholder="Select method" />
                  </SelectTrigger>
                  <SelectContent>
                    {PREFERRED_CONTACT_METHOD_OPTIONS.map((method) => (
                      <SelectItem key={method} value={method}>
                        {method}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-jobTitle">Job Title</Label>
              <Input
                id="edit-jobTitle"
                value={editForm.jobTitle}
                onChange={(e) => setEditForm({ ...editForm, jobTitle: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-company">Company</Label>
              <Input
                id="edit-company"
                value={editForm.company}
                onChange={(e) => setEditForm({ ...editForm, company: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-notes">Notes</Label>
              <Textarea
                id="edit-notes"
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                rows={3}
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={editForm.isPrimary}
                onCheckedChange={(checked) => setEditForm({ ...editForm, isPrimary: checked === true })}
              />
              Set as primary contact
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditOpen(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleEditSubmit} disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Contact</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedContact ? contactDisplayName(selectedContact) : 'this contact'}?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Column configuration */}
      <ColumnConfigDialog
        open={isColumnConfigOpen}
        onOpenChange={setIsColumnConfigOpen}
        columns={columns}
        onApply={savePreferences}
        onReset={resetToDefaults}
      />
    </div>
  );
}

function DetailRow({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      <span className="text-muted-foreground">{label}</span>
      <span className="col-span-2 break-words">{children}</span>
    </div>
  );
}

function ContactsPageWrapper() {
  return (
    <ExportProvider>
      <ContactsPage />
    </ExportProvider>
  );
}

export default ContactsPageWrapper;
